import {
  useAddCandidatesToVacancyMutation,
  useInitAssessmentCvMutation,
  useUploadCandidatesToVacancyMutation,
} from "@generated/graphql";
import { Button, Stack } from "@mui/material";
import AddCandidateModal from "components/AddCandidateModal";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { useContext, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingButton } from "@mui/lab";
import { parseApolloError } from "helpers/errorsParser";
import { CandidateResultResponse, VacancyCompanyCandidatesRefetch } from "../types";
import UploadCandidateModal from "./UploadCandidateModal";
import CandidateResultModal from "./CandidateResultModal";

interface ControlsProps {
  vacancyCompanyCandidatesRefetch: VacancyCompanyCandidatesRefetch | null;
}

const Controls = ({ vacancyCompanyCandidatesRefetch }: ControlsProps) => {
  const { vacancyId } = useParams();
  const [isAddCandidateModalOpen, setIsAddCandidateModalOpen] = useState<boolean>(false);
  const [isUploadCandidateModalOpen, setIsUploadCandidateModalOpen] = useState<boolean>(false);
  const [isCandidateResultModalOpen, setIsCandidateResultModalOpen] = useState<boolean>(false);
  const [candidateResult, setCandidateResult] = useState<CandidateResultResponse>(null);
  const { addToast } = useContext(ToastContext);
  const [addCandidatesToVacancy] = useAddCandidatesToVacancyMutation();
  const [uploadCandidatesToVacancy, { loading: uploadCandidatesToVacancyLoading }] =
    useUploadCandidatesToVacancyMutation();
  const [initAssessmentCv, { loading: initAssessmentCvLoading }] = useInitAssessmentCvMutation();

  const onSubmitAddCandidateModal = (companyCandidateIds: string[]) => {
    addCandidatesToVacancy({
      variables: {
        input: {
          vacancyId: vacancyId!,
          companyCandidateIds,
        },
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS });
        vacancyCompanyCandidatesRefetch?.();
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const transformDataForXLSX = (data: CandidateResultResponse) => {
    const dataForXlsx = data?.map((item) => ({
      ID: item.numericId,
      ФИО: item.fullName,
      Логин: item.login,
      Пароль: item.password,
    }));

    return dataForXlsx || [];
  };

  const activeResultModal = (data: CandidateResultResponse) => {
    setIsUploadCandidateModalOpen(false);
    setCandidateResult(data);
    setIsCandidateResultModalOpen(true);
  };

  const onSubmitUploadCandidateModal = (files: File[]) => {
    uploadCandidatesToVacancy({
      variables: {
        files,
        vacancyId: vacancyId!,
      },
    })
      .then(({ data }) => {
        addToast({ type: ToastTypeEnum.SUCCESS });
        vacancyCompanyCandidatesRefetch?.();
        activeResultModal(data?.createVacancyCompanyCandidateByCVs);
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  const handleInitAssessmentCv = () => {
    initAssessmentCv({
      variables: {
        vacancyId: vacancyId!,
      },
    })
      .then(() => {
        addToast({ type: ToastTypeEnum.SUCCESS, text: "Проверка CV запущена" });
      })
      .catch((e) => parseApolloError(e, { addToast }));
  };

  return (
    <>
      <Stack
        spacing={2.5}
        sx={{ width: 256, marginLeft: "auto" }}>
        <Button
          variant='contained'
          onClick={() => setIsUploadCandidateModalOpen(true)}>
          Загрузить кандидатов
        </Button>
        <Button
          variant='outlined'
          color='secondary'
          onClick={() => setIsAddCandidateModalOpen(true)}>
          Добавить кандидатов
        </Button>
        <LoadingButton
          variant='contained'
          color='success'
          loading={initAssessmentCvLoading}
          onClick={handleInitAssessmentCv}>
          Запустить проверку CV
        </LoadingButton>
      </Stack>
      {isAddCandidateModalOpen && (
        <AddCandidateModal
          open={isAddCandidateModalOpen}
          onClose={() => setIsAddCandidateModalOpen(false)}
          onSubmit={onSubmitAddCandidateModal}
        />
      )}
      {isUploadCandidateModalOpen && (
        <UploadCandidateModal
          open={isUploadCandidateModalOpen}
          isLoading={uploadCandidatesToVacancyLoading}
          onClose={() => setIsUploadCandidateModalOpen(false)}
          onSubmit={onSubmitUploadCandidateModal}
        />
      )}
      {isCandidateResultModalOpen && (
        <CandidateResultModal
          data={transformDataForXLSX(candidateResult)}
          open={isCandidateResultModalOpen}
          onClose={() => setIsCandidateResultModalOpen(false)}
        />
      )}
    </>
  );
};

export default Controls;
